"use client";

import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useEffect, useRef, useState, useTransition } from "react";
import type {
	CheckinAnswer,
	CheckinCandidate,
} from "@/lib/applications/outcome-checkin";
import { useSession } from "@/lib/auth/client";
import { useStickyFooterClaimed } from "@/lib/monetization/sticky-footer-claim";
import enCopy from "@/locales/en/outcome-checkin.json";
import idCopy from "@/locales/id/outcome-checkin.json";
import { submitInAppOutcome } from "@/server/actions/applications-outcome";
import { getOutcomeCheckin } from "@/server/actions/outcome-checkin";

/** Per-device "don't ask about this tracker until <epoch ms>" memory. */
const SKIP_KEY = "ld_outcome_checkin_skip";
const DAY_MS = 24 * 60 * 60 * 1000;
const SKIP_LATER_MS = 3 * DAY_MS;
const SKIP_NOT_APPLIED_MS = 365 * DAY_MS;
/** Let the page settle first; the check-in is never the first thing seen. */
const SHOW_DELAY_MS = 2500;

type SkipMap = Record<string, number>;

function readSkips(now: number): SkipMap {
	try {
		const raw = window.localStorage.getItem(SKIP_KEY);
		const parsed: unknown = raw ? JSON.parse(raw) : {};
		if (!parsed || typeof parsed !== "object") return {};
		const out: SkipMap = {};
		for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
			if (typeof v === "number" && v > now) out[k] = v;
		}
		return out;
	} catch {
		return {};
	}
}

function writeSkip(trackerId: string, ms: number) {
	try {
		const now = Date.now();
		const skips = readSkips(now);
		skips[trackerId] = now + ms;
		window.localStorage.setItem(SKIP_KEY, JSON.stringify(skips));
	} catch {
		// Storage blocked (private mode): the ask may repeat next visit. Fine.
	}
}

function fill(template: string, vars: Record<string, string>): string {
	return template.replace(/\{(\w+)\}/g, (_, k: string) => vars[k] ?? "");
}

type Status = "idle" | "done" | "error";

/**
 * Coach-toned "did they reply?" check-in (flag `outcome-capture-v2`, owner
 * chat 2026-10-29). Mounted once in the locale layout so it reaches the
 * seeker wherever they land — the old in-app ask lived only on
 * `/applications` (20 visitors in 30 days). Signed-in only; asks about ONE
 * application per visit at ~day 7 and ~day 14 (cadence:
 * `src/lib/applications/outcome-checkin.ts`). Answers write
 * `application_outcomes` through `submitInAppOutcome`.
 */
export function OutcomeCheckinCard({ locale }: { locale?: string }) {
	const t = locale === "en" ? enCopy : idCopy;
	const posthog = usePostHog();
	const pathname = usePathname() ?? "";
	const { data: session } = useSession();
	const userId = session?.user?.id ?? null;
	const [candidate, setCandidate] = useState<CheckinCandidate | null>(null);
	const [status, setStatus] = useState<Status>("idle");
	const [answered, setAnswered] = useState<CheckinAnswer | null>(null);
	const [isPending, startTransition] = useTransition();
	const inFlight = useRef(false);
	const fetched = useRef(false);
	const footerClaimed = useStickyFooterClaimed();
	// `/applications` carries its own outcome prompts: never ask twice there.
	const onTrackerPage = pathname.includes("/applications");

	useEffect(() => {
		if (!userId || fetched.current || onTrackerPage) return;
		fetched.current = true;
		let cancelled = false;
		const timer = setTimeout(async () => {
			const skip = Object.keys(readSkips(Date.now()));
			const res = await getOutcomeCheckin(skip).catch(() => null);
			if (cancelled || !res?.enabled || !res.candidate) return;
			// One prompt at a time: the apply-confirm toast owns the slot.
			if (document.querySelector('[data-testid="confirm-apply-toast"]')) {
				return;
			}
			setCandidate(res.candidate);
			posthog?.capture("outcome_checkin_shown", {
				job_id: res.candidate.jobId,
				stage: res.candidate.stage,
				kind: res.candidate.kind,
			});
		}, SHOW_DELAY_MS);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [userId, onTrackerPage, posthog]);

	// Never cover a page-owned sticky footer (the mobile Apply bar): the
	// check-in waits for the next page instead.
	if (!candidate || footerClaimed) return null;

	const atCompany = candidate.company
		? fill(t.atCompany, { company: candidate.company })
		: "";
	const question = fill(
		candidate.kind === "clicked"
			? t.askClicked
			: candidate.stage === "d14"
				? t.askD14
				: t.askD7,
		{ title: candidate.title, atCompany },
	);

	const capture = (answer: CheckinAnswer | "dismiss") =>
		posthog?.capture("outcome_checkin_answered", {
			job_id: candidate.jobId,
			stage: candidate.stage,
			kind: candidate.kind,
			answer,
		});

	const answer = (ans: CheckinAnswer) => {
		if (inFlight.current) return;
		inFlight.current = true;
		capture(ans);
		if (ans === "not_applied") {
			// Self-report only, same as the email/toast "didn't apply" answer:
			// no outcome row; just never ask about this one again here.
			writeSkip(candidate.trackerId, SKIP_NOT_APPLIED_MS);
			setAnswered(ans);
			setStatus("done");
			inFlight.current = false;
			return;
		}
		startTransition(async () => {
			try {
				const res = await submitInAppOutcome(candidate.trackerId, ans);
				if (res.success) {
					writeSkip(candidate.trackerId, DAY_MS);
					setAnswered(ans);
					setStatus("done");
				} else {
					setStatus("error");
				}
			} catch {
				setStatus("error");
			} finally {
				inFlight.current = false;
			}
		});
	};

	const dismiss = () => {
		capture("dismiss");
		writeSkip(candidate.trackerId, SKIP_LATER_MS);
		setCandidate(null);
	};

	const focus =
		"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";
	const btn = `min-h-11 rounded-lg border border-border bg-background px-3 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-60 ${focus}`;

	const thanks =
		answered === "interview"
			? t.thanksInterview
			: answered === "no"
				? t.thanksRejected
				: answered === "not_applied"
					? t.thanksNotApplied
					: t.thanksWaiting;

	return (
		<section
			aria-label={t.label}
			data-testid="outcome-checkin-card"
			data-status={status}
			data-stage={candidate.stage}
			className="fixed inset-x-4 bottom-[calc(var(--sticky-cta-h,0px)+var(--ad-bar-h,0px)+1rem)] z-50 mx-auto block max-w-sm rounded-xl border border-border bg-card p-4 text-card-foreground shadow-lg sm:right-auto sm:left-4 lg:bottom-4"
		>
			{status === "done" ? (
				<div className="flex flex-col gap-2">
					<p
						data-testid="outcome-checkin-thanks"
						className="text-sm text-foreground"
					>
						{thanks}
					</p>
					<button
						type="button"
						onClick={() => setCandidate(null)}
						className={`self-end text-xs text-muted-foreground underline-offset-4 hover:underline ${focus}`}
					>
						OK
					</button>
				</div>
			) : (
				<div aria-busy={isPending} className="flex flex-col gap-3">
					<p className="text-sm font-medium text-foreground">{question}</p>
					<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
						<button
							type="button"
							data-testid="outcome-checkin-interview"
							disabled={isPending}
							onClick={() => answer("interview")}
							className={btn}
						>
							{t.interview}
						</button>
						<button
							type="button"
							data-testid="outcome-checkin-still-waiting"
							disabled={isPending}
							onClick={() => answer("still_waiting")}
							className={btn}
						>
							{t.stillWaiting}
						</button>
						<button
							type="button"
							data-testid="outcome-checkin-no"
							disabled={isPending}
							onClick={() => answer("no")}
							className={btn}
						>
							{t.rejected}
						</button>
						{candidate.kind === "clicked" ? (
							<button
								type="button"
								data-testid="outcome-checkin-not-applied"
								disabled={isPending}
								onClick={() => answer("not_applied")}
								className={btn}
							>
								{t.notApplied}
							</button>
						) : null}
					</div>
					{status === "error" ? (
						<p
							role="alert"
							className="rounded border border-destructive/30 bg-destructive/10 px-2 py-1 text-xs font-medium text-destructive"
						>
							{t.error}
						</p>
					) : null}
					<button
						type="button"
						data-testid="outcome-checkin-dismiss"
						onClick={dismiss}
						className={`self-start text-xs text-muted-foreground underline-offset-4 hover:underline ${focus}`}
					>
						{t.dismiss}
					</button>
				</div>
			)}
		</section>
	);
}
